/* Read SENSEA-THEME.md before editing. Rules are enforced by CI. */
/* sensea email sign-up (blocks/sensea-signup.liquid). Sends the email to Klaviyo's public sign-up endpoint for one
   list. The list's double opt-in sends the confirmation email; nothing here sets cookies or tracks anyone.
   Without this script the form falls back to Shopify's customer form. */

const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const REVISION = '2024-10-15';

async function subscribe(form) {
  const key = form.dataset.klaviyoKey;
  // German pages go to the German list: its double opt-in email and flows are in German.
  const german = (form.dataset.locale || '').startsWith('de') && form.dataset.klaviyoListDe;
  const list = german ? form.dataset.klaviyoListDe : form.dataset.klaviyoList;
  const input = form.querySelector('input[type="email"]');
  const email = input.value.trim();
  const campaign = (new URLSearchParams(window.location.search).get('utm_campaign') || '').slice(0, 80);
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        // Visits from our ads carry utm_campaign (the Meta campaign name) in the page address. Adding it to the source
        // lets the ads dashboard count confirmed sign-ups per campaign. Read from this page's address only; nothing is stored.
        custom_source: (form.dataset.source || 'Website') + (german ? ' (DE)' : '') + (campaign ? ' · ' + campaign : ''),
        profile: {
          data: {
            type: 'profile',
            // Only the email: the public endpoint rejects a 'subscriptions' field. Joining the list is the consent,
            // and the list's double opt-in confirms it.
            attributes: { email },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: list } } },
    },
  };
  const res = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
}

/* Opens the block's "check your inbox" popup (Horizon's dialog-component). Returns false if it can't. */
function openPopup(form, email) {
  const popup = form.closest('.sensea-signup')?.querySelector('dialog-component');
  if (!popup || typeof popup.showDialog !== 'function') return false;
  const sent = popup.querySelector('.sensea-signup__popup-sent');
  const slot = popup.querySelector('[data-sensea-signup-email]');
  if (sent && slot && email) {
    slot.textContent = email;
    sent.hidden = false;
  }
  popup.showDialog();
  return true;
}

function init(form) {
  if (form.dataset.ready || !form.dataset.klaviyoKey || !form.dataset.klaviyoList) return;
  form.dataset.ready = 'true';
  const input = form.querySelector('input[type="email"]');
  const button = form.querySelector('button[type="submit"]');
  const error = form.querySelector('.sensea-signup__error');
  const done = form.querySelector('.sensea-signup__done[hidden]');
  if (!input || !button || !done) return;
  button.disabled = false;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!input.checkValidity()) {
      input.reportValidity();
      return;
    }
    button.disabled = true;
    form.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;
    try {
      await subscribe(form);
      // Tell Shopify Customer Events that a sign-up went through (R9: tracking only via Customer Events, consent-gated).
      // A custom pixel in Shopify admin may forward it, e.g. to Meta as "Lead". No third-party code or personal data here.
      window.Shopify?.analytics?.publish?.('sensea_email_signup', {
        source: form.dataset.source || 'Website',
        locale: form.dataset.locale || '',
      });
      [...form.children].forEach((el) => {
        if (el !== done) el.hidden = true;
      });
      done.hidden = false;
      done.setAttribute('tabindex', '-1');
      if (!openPopup(form, input.value.trim())) done.focus({ preventScroll: true });
    } catch (e) {
      if (error) {
        error.textContent = error.dataset.errorText || 'Please try again.';
        error.hidden = false;
      }
      input.setAttribute('aria-invalid', 'true');
    } finally {
      button.disabled = false;
      form.removeAttribute('aria-busy');
    }
  });
}

document.querySelectorAll('form.sensea-signup__form').forEach(init);
document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('form.sensea-signup__form').forEach(init));

/* Klaviyo's success page sends confirmed subscribers to ?subscribed=1: show "You're on the list" in the first
   sign-up popup on the page, then drop the parameter so a reload doesn't show it again. */
function openConfirmed() {
  const query = new URLSearchParams(location.search);
  if (!query.has('subscribed')) return;
  const popup = document.querySelector('.sensea-signup dialog-component');
  if (!popup) return;
  customElements.whenDefined('dialog-component').then(() => {
    const title = popup.querySelector('.sensea-signup__popup-title');
    const text = popup.querySelector('.sensea-signup__popup-text');
    if (title && popup.dataset.confirmedTitle) title.textContent = popup.dataset.confirmedTitle;
    if (text && popup.dataset.confirmedText) text.textContent = popup.dataset.confirmedText;
    popup.showDialog();
    query.delete('subscribed');
    const rest = query.toString();
    history.replaceState(null, '', location.pathname + (rest ? `?${rest}` : '') + location.hash);
  });
}

openConfirmed();
